import React, { useState } from 'react';
import { getHeaderSortClassName } from 'helpers/tableSort';
import Truncate from 'react-truncate';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import { FullProduct } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import formatNumber, {
  formatCurrency,
  formatPercentage
} from 'helpers/formatNumber';
import { Maths } from 'helpers/maths';

interface LinkCellProps {
  product: FullProduct;
  onViewMore: (product: FullProduct) => void;
}

const LinkCell = ({ product, onViewMore }: LinkCellProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="flex justify-center items-center">
      <button
        type="button"
        className={`flex flex-col cursor-pointer py-10px px-8px rounded focus:outline-none
 ${isHovered ? 'bg-blue-10' : ''}`}
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onViewMore(product)}
      >
        <div className="flex gap-1 justify-center">
          <OpenInNewIcon
            className={isHovered ? 'text-blue-50' : 'text-neutral-60'}
          />
          <div className={`${isHovered ? 'text-blue-50' : 'text-dark-grey'}`}>
            View More
          </div>
        </div>
      </button>
    </div>
  );
};

const Columns = (
  sortBy: string,
  sortDir: string,
  hasTankInformation: boolean,
  onViewMore: (product: FullProduct) => void
) => {
  const formatCell = (
    value: number | null | undefined,
    unit: string,
    unitSuffix = '',
    decimals = 2
  ) => {
    return value !== null && value !== undefined
      ? formatNumber(value, getAbbreviatedUnit(unit), decimals) + unitSuffix
      : '---';
  };

  const columns: Array<any> = [
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      height: 60,
      width: 200,
      fixed: true,
      className: getHeaderSortClassName('productName', sortBy, sortDir),
      sorter: (a: any, b: any) => a.productName.localeCompare(b.productName),
      sortOrder: sortBy === 'productName' && sortDir,
      render: (_: any, record: FullProduct) => {
        const nameRow = (
          <div
            className="text-neutral-90 mt-1 font-medium"
            style={{ maxWidth: '100%' }}
          >
            <Truncate ellipsis={<span title={record.productName}>…</span>}>
              {record.productName}
            </Truncate>
          </div>
        );
        const manufacturerRow = (classes = '') => (
          <div
            className={`text-neutral-60 mb-1 ${classes}`}
            style={{ maxWidth: '100%' }}
          >
            <Truncate ellipsis={<span title={record.manufacturerName}>…</span>}>
              {record.manufacturerName}
            </Truncate>
          </div>
        );
        return (
          <>
            {nameRow}
            {manufacturerRow('text-xs')}
          </>
        );
      }
    },
    {
      title: 'Rate per Acre',
      dataIndex: 'ratePerAcre',
      key: 'ratePerAcre',
      height: 60,
      width: 150,
      className: `${getHeaderSortClassName(
        'ratePerAcre',
        sortBy,
        sortDir
      )} justify-end`,
      sorter: (a: any, b: any) => a.ratePerAreaValue - b.ratePerAreaValue,
      sortOrder: sortBy === 'ratePerAcre' && sortDir,
      render: (_: any, record: FullProduct) => (
        <div className="text-dark-grey mt-1 font-medium text-right">
          {formatCell(record.ratePerAreaValue, record.ratePerAreaUnit, '/ac')}
        </div>
      )
    }
  ];

  if (hasTankInformation) {
    columns.push({
      title: 'Rate per Tank',
      dataIndex: 'ratePerTank',
      key: 'ratePerTank',
      height: 60,
      width: 150,
      sorter: (a: any, b: any) => a.ratePerTankValue - b.ratePerTankValue,
      sortOrder: sortBy === 'ratePerTank' && sortDir,
      className: `${getHeaderSortClassName(
        'ratePerTank',
        sortBy,
        sortDir
      )} justify-end`,
      render: (_: any, record: FullProduct) => {
        return (
          <div className="text-dark-grey mt-1 font-medium text-right">
            {formatCell(
              record.ratePerTankValue,
              record.ratePerTankUnit,
              '/tank'
            )}
          </div>
        );
      }
    });
  }

  columns.push(
    {
      title: 'Total Product',
      dataIndex: 'totalProduct',
      key: 'totalProduct',
      height: 60,
      width: 150,
      sorter: (a: any, b: any) => a.totalProductValue - b.totalProductValue,
      sortOrder: sortBy === 'totalProduct' && sortDir,
      className: `${getHeaderSortClassName(
        'totalProduct',
        sortBy,
        sortDir
      )} justify-end`,
      render: (_: any, record: FullProduct) => (
        <div className="text-dark-grey mt-1 font-medium text-right">
          {formatCell(record.totalProductValue, record.totalProductUnit)}
        </div>
      )
    },
    {
      title: 'Appl. Acres',
      dataIndex: 'appliedAcres',
      key: 'appliedAcres',
      height: 60,
      width: 150,
      sorter: (a: any, b: any) => a.appliedAreaValue - b.appliedAreaValue,
      sortOrder: sortBy === 'appliedAcres' && sortDir,
      className: `${getHeaderSortClassName(
        'appliedAcres',
        sortBy,
        sortDir
      )} justify-end`,
      render: (_: any, record: FullProduct) => {
        const upperRow = (
          <div
            className="text-neutral-90 mt-1 font-medium text-right"
            style={{ maxWidth: '100%' }}
          >
            {formatCell(record.appliedAreaValue, record.appliedAreaUnit)}
          </div>
        );
        const lowerRow = (
          <div
            className="text-neutral-60 mb-1 text-xs text-right"
            style={{ maxWidth: '100%' }}
          >
            {record.appliedAreaCoveragePercent
              ? formatPercentage(
                  Maths.divide(record.appliedAreaCoveragePercent, 100)
                )
              : '---'}
          </div>
        );
        return (
          <>
            {upperRow}
            {lowerRow}
          </>
        );
      }
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      height: 60,
      width: 150,
      sorter: (a: any, b: any) =>
        (a.totalCostValue ?? 0) - (b.totalCostValue ?? 0),
      sortOrder: sortBy === 'cost' && sortDir,
      className: `${getHeaderSortClassName(
        'cost',
        sortBy,
        sortDir
      )} justify-end`,
      render: (_: any, record: FullProduct) => {
        if (!record.totalCostValue && !record.priceValue) {
          return (
            <div
              className="text-neutral-90 mt-1 font-medium text-right"
              style={{ maxWidth: '100%' }}
            >
              ---
            </div>
          );
        }
        const upperRow = (
          <div
            className="text-neutral-90 mt-1 font-medium text-right"
            style={{
              maxWidth: '100%'
            }}
          >
            {record.totalCostValue
              ? formatCurrency(record.totalCostValue)
              : '---'}
          </div>
        );
        const lowerRow = (
          <div
            className="text-neutral-60 mb-1 text-xs text-right"
            style={{
              maxWidth: '100%'
            }}
          >
            {record.priceValue
              ? `${formatCurrency(record.priceValue)}/${getAbbreviatedUnit(
                  record.priceUnit
                )}`
              : '---'}
          </div>
        );
        return (
          <>
            {upperRow}
            {lowerRow}
          </>
        );
      }
    },
    {
      title: '',
      dataIndex: 'link',
      key: 'link',
      className: 'application-view-product-link-cell',
      height: 60,
      width: 123,
      render: (_: any, record: FullProduct) => (
        <LinkCell product={record} onViewMore={onViewMore} />
      )
    }
  );

  return columns;
};
export default Columns;
